import moment from 'moment-timezone'
import { COUNTRY_TO_CURRENCY } from './countryCurrencyMap'
import { ZONE_PRIMARY_COUNTRY } from './zonePrimaryCountry'

export const DEFAULT_CURRENCY = 'USD'

/**
 * Resolve the user's IANA timezone with the following priority:
 *   1. explicit value passed by the caller (Vuex: user.userData.timezone)
 *   2. localStorage `userData` JSON blob (written on login/signup)
 *   3. browser-detected timezone via Intl
 */
export function resolveTimezone(preferredTimezone) {
  if (preferredTimezone && typeof preferredTimezone === 'string') {
    return preferredTimezone
  }
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    if (userData && typeof userData.timezone === 'string' && userData.timezone) {
      return userData.timezone
    }
  } catch (e) {
    // corrupted localStorage entry — ignore and fall through
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch (e) {
    return null
  }
}

/**
 * IANA timezone → ISO 3166-1 alpha-2 country code.
 *
 * moment-timezone ships the tz database country list, but returns it
 * alphabetically rather than primary-country-first — so the first entry is
 * only right by luck (Asia/Tokyo lists AU before JP, Asia/Riyadh starts at
 * AQ). ZONE_PRIMARY_COUNTRY pins the primary country for every zone where
 * that ordering picks the wrong one, or where the tz database no longer
 * carries any country metadata at all.
 */
export function timezoneToCountry(timezone) {
  if (!timezone) return null
  if (ZONE_PRIMARY_COUNTRY[timezone]) return ZONE_PRIMARY_COUNTRY[timezone]
  try {
    const zone = moment.tz.zone(timezone)
    const countries = zone && typeof zone.countries === 'function' ? zone.countries() : null
    return countries && countries.length ? countries[0] : null
  } catch (e) {
    return null
  }
}

/** ISO 3166-1 alpha-2 country code → ISO 4217 currency code. */
export function countryToCurrency(countryCode) {
  if (!countryCode) return null
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || null
}

/**
 * Full detection chain: timezone → country → currency.
 * Never throws; falls back to USD when any step cannot be resolved.
 */
export function detectCurrency(preferredTimezone) {
  const timezone = resolveTimezone(preferredTimezone)
  const country = timezoneToCountry(timezone)
  const currency = countryToCurrency(country) || DEFAULT_CURRENCY
  return { timezone, country, currency }
}
